import type {
    CustomerFilter,
    CustomerSortBy,
    CustomerStatus,
    InsightFilter,
    InsightType,
    NoteFilter,
    SalesDocumentsFilter,
    SalesMetricFilter,
} from '@plato/app-core/gql/schema-types';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import {
    generateQueryParamsString,
    jsonParam,
    queryParamsConfig,
    stringArrayParam,
    stringParam,
    transientParam,
} from '@plato/ui-legacy/lib/query-params';
import type { ColumnSort } from '@plato/ui/components/data-table';

export type CustomerListColumnId =
    | 'customerName'
    | 'status'
    | 'documentedVisits'
    | 'visitsPer100kRevenue'
    | 'lastVisitDate'
    | 'opportunityInsightsAmount'
    | 'riskInsightsAmount'
    | 'salesMetric'
    | 'salesMetricDelta'
    | 'lastOrderDate'
    | 'salesReps'
    | 'city'
    | 'industrySector'
    | 'grossMargin'
    | 'grossMarginPercent'
    | 'grossMarginDelta';

export type CustomerListSorting = ColumnSort<CustomerListColumnId>;

export type CustomerListParams = CustomerFilter & {
    sorting: CustomerListSorting;
};

export const defaultCustomerListSorting: CustomerListSorting = { id: 'salesMetric', direction: 'desc' };

export const opportunityInsightsFilter: InsightFilter = {
    types: ['XSellWebCategory', 'FirstPurchaseWebCat3', 'ShareOfWalletCategory'],
};

export const riskInsightsFilter: InsightFilter = {
    types: ['ChurnWarningWebCategory'],
};

export const opportunityInsightsSortBy: CustomerSortBy = {
    insights: { dimension: opportunityInsightsFilter, field: 'total_sum' },
};

export const riskInsightsSortBy: CustomerSortBy = {
    insights: { dimension: riskInsightsFilter, field: 'total_sum' },
};

export const customersExpansionPotentialFilter: CustomerListParams = {
    sorting: { id: 'opportunityInsightsAmount', direction: 'desc' },
};

export const customersRevenueDeclineFilter: CustomerListParams = {
    insightTypes: ['ChurnWarningWebCategory'],
    sorting: { id: 'riskInsightsAmount', direction: 'asc' },
};

export const ttmRevenueFilter: SalesMetricFilter = {
    basis: SalesMetricBasis.BILLED,
    range: { preset: 'TTM' },
};
export const ordersFilter: SalesDocumentsFilter = { types: ['ORDER'] };

export const visitsFilter: NoteFilter = {
    types: ['VISIT'],
    range: { preset: 'TTM' },
    interactionHappened: true,
};

export const customerListParamsConfig = queryParamsConfig<CustomerListParams>({
    ids: stringArrayParam(),
    companyIds: stringArrayParam(),
    salesRepIds: stringArrayParam(),
    statuses: stringArrayParam<CustomerStatus[]>(),
    insightTypes: stringArrayParam<InsightType[]>(),
    cityNames: stringArrayParam(),
    zipCodes: stringArrayParam(),
    industryNames: stringArrayParam(),
    customerClasses: stringArrayParam(),
    salesRegions: stringArrayParam(),
    searchString: transientParam(stringParam()),
    sorting: jsonParam<CustomerListSorting>(),
});

export function generateSearchParamsForCustomerList(params: Partial<CustomerListParams>): string {
    return generateQueryParamsString(customerListParamsConfig, params);
}
